import * as React from 'react';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, {listItemButtonClasses} from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Link from '@mui/joy/Link';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ColorSchemeToggle from './ColorSchemeToggle';
import {closeSidebar} from './utils';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {Menu} from "./Menu";
import {useLocation, useNavigate} from "react-router-dom";
import {useAuth, useRole} from "../auth/useAuth";
import {UserSessionData} from '../../app/userSessionData';
import {MenuItem} from './Menu';

function Toggler({
                     defaultExpanded = false,
                     renderToggle,
                     children,
                 }: {
    defaultExpanded?: boolean;
    children: React.ReactNode;
    renderToggle: (params: {
        open: boolean;
        setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    }) => React.ReactNode;
}) {
    const [open, setOpen] = React.useState(defaultExpanded);
    return (
        <React.Fragment>
            {renderToggle({open, setOpen})}
            <Box
                sx={[
                    {
                        display: 'grid',
                        transition: '0.2s ease',
                        '& > *': {
                            overflow: 'hidden',
                        },
                    },
                    open ? {gridTemplateRows: '1fr'} : {gridTemplateRows: '0fr'},
                ]}
            >
                {children}
            </Box>
        </React.Fragment>
    );
}

export default function Sidebar() {

    const navigate = useNavigate()
    const location = useLocation();

    React.useEffect(() => {
        Menu.forEach(value => {
            findActiveMenu(value);
        });
    }, [location]);


    const findActiveMenu=(value:MenuItem)=> {
        if ((`/app/${value.Rout}` === location.pathname) || (value.Rout === '' && (location.pathname === '/app' || location.pathname === '/app/'))) {
            setSelectedIndex(value.Id);
            return;
        }
        if (value.Items && value.Items.length > 0) {
            value.Items.forEach(value => {
                findActiveMenu(value);
            });
        }
    }
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const role = useRole();
    const auth = useAuth();

    return (
        <Sheet
            className="Sidebar"
            sx={{
                position: {xs: 'fixed', md: 'sticky'},
                transform: {
                    xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
                    md: 'none',
                },
                transition: 'transform 0.4s, width 0.4s',
                zIndex: 10000,
                height: '100dvh',
                width: 'var(--Sidebar-width)',
                top: 0,
                p: 2,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                borderRight: '1px solid',
                borderColor: 'divider',
            }}
        >
            <GlobalStyles
                styles={(theme) => ({
                    ':root': {
                        '--Sidebar-width': '220px',
                        [theme.breakpoints.up('lg')]: {
                            '--Sidebar-width': '240px',
                        },
                    },
                })}
            />
            <Box
                className="Sidebar-overlay"
                sx={{
                    position: 'fixed',
                    zIndex: 9998,
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    opacity: 'var(--SideNavigation-slideIn)',
                    backgroundColor: 'var(--joy-palette-background-backdrop)',
                    transition: 'opacity 0.4s',
                    transform: {
                        xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                        lg: 'translateX(-100%)',
                    },
                }}
                onClick={() => closeSidebar()}
            />
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                <IconButton variant="soft" color="primary" size="sm">
                    <AdminPanelSettingsIcon/>
                </IconButton>
                <Typography level="title-lg">Steam Trader</Typography>
                <ColorSchemeToggle sx={{ml: 'auto'}}/>
            </Box>
            <Box
                sx={{
                    minHeight: 0,
                    overflow: 'hidden auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    [`& .${listItemButtonClasses.root}`]: {
                        gap: 1.5,
                    },
                }}
            >
                <List
                    size="sm"
                    sx={{
                        gap: 1,
                        '--List-nestedInsetStart': '30px',
                        '--ListItem-radius': (theme) => theme.vars.radius.sm,
                    }}
                >
                    {Menu.filter(menu => menu.Role.includes(role)).map((menu) => (
                        (menu.Items && menu.Items.length > 0) ?
                            <ListItem nested key={menu.Id}>
                                <Toggler defaultExpanded={false}
                                    renderToggle={({open, setOpen}) => (
                                        <ListItemButton onClick={() => setOpen(!open)}>
                                            {menu.GetIcon && menu.GetIcon()}
                                            <ListItemContent>
                                                <Typography level="title-sm">{menu.Name}</Typography>
                                            </ListItemContent>
                                            <KeyboardArrowDownIcon
                                                sx={[
                                                    open
                                                        ? {
                                                            transform: 'rotate(180deg)',
                                                        }
                                                        : {
                                                            transform: 'none',
                                                        },
                                                ]}
                                            />
                                        </ListItemButton>
                                    )}
                                >
                                    <List sx={{gap: 0.5}}>
                                        {menu.Items.filter(i => i.Role.includes(role)).map(item => (
                                            <ListItem key={item.Id}>
                                                <ListItemButton selected={selectedIndex === item.Id} onClick={() => {
                                                    if (item.Rout) {
                                                        navigate(item.Rout);
                                                        setSelectedIndex(item.Id);
                                                    }
                                                }}>
                                                    {item.Name}
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Toggler>
                            </ListItem>
                            :
                            <ListItem key={menu.Id}>
                                <ListItemButton selected={selectedIndex === menu.Id} onClick={() => {
                                    if (menu.Rout !== undefined) {
                                        navigate(menu.Rout === '' ? '/app' : menu.Rout);
                                        setSelectedIndex(menu.Id);
                                    }
                                }}>
                                    {menu.GetIcon && menu.GetIcon()}
                                    <ListItemContent>
                                        <Typography level="title-sm">{menu.Name}</Typography>
                                    </ListItemContent>
                                </ListItemButton>
                            </ListItem>
                    ))}
                </List>
                <List
                    size="sm"
                    sx={{
                        mt: 'auto',
                        flexGrow: 0,
                        '--ListItem-radius': (theme) => theme.vars.radius.sm,
                        '--List-gap': '8px',
                        mb: 2,
                    }}
                >
                    <ListItem>
                        {'admin.manager'.includes(role)
                        ?<ListItemButton selected={selectedIndex === 99} onClick={() => {
                            navigate('settings');
                            setSelectedIndex(99);
                        }}>
                            <SettingsRoundedIcon/>
                            Настройки
                        </ListItemButton>
                        :<></>}
                    </ListItem>
                </List>
            </Box>
            <Divider/>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                <Avatar
                    variant="outlined"
                    size="sm"
                    src={auth.user.avatarUrl}
                />
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Link
                        underline="hover"
                        href={`https://steamcommunity.com/profiles/${auth.user.userName}/`}
                        target="_blank"
                        sx={{fontSize: 12, fontWeight: 500}}
                    >
                        <Typography level="title-sm">{auth.user.name}</Typography>
                    </Link>
                    <Typography level="body-xs">
                        <Link
                            underline="hover"
                            href={`https://steamcommunity.com/profiles/${auth.user.userName}/`}
                            target="_blank"
                            sx={{fontSize: 12, fontWeight: 500}}
                        >
                            {auth.user.userName}
                        </Link>
                    </Typography>
                </Box>
                <IconButton size="sm" variant="plain" color="neutral" onClick={() => {
                    auth.signout(() => {
                        UserSessionData.getInstance().clearData();
                    });
                    navigate('/')
                }
                }>
                    <LogoutRoundedIcon/>
                </IconButton>
            </Box>
        </Sheet>
    );
}
